import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, Building2, Search, ArrowLeft, Filter, RefreshCw, X, Eye
} from 'lucide-react';
import { eventGymnastService } from '../../services/supabaseService';
import { imageServiceProxy, storageServiceProxy } from '../../services/proxyService';
import { calculateAge } from '../../utils/formatters';
import './OrganizerEventAthletes.css';

const OrganizerEventAthletes = ({ event, onBack }) => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clubImages, setClubImages] = useState({});
  const [eventImageUrl, setEventImageUrl] = useState(null);

  // Modal state
  const [selectedClub, setSelectedClub] = useState(null);
  const [modalFilterLevel, setModalFilterLevel] = useState('all');
  const [modalSearch, setModalSearch] = useState('');

  // Load event image
  const loadEventImage = useCallback(async () => {
    try {
      if (event.image_url) {
        setEventImageUrl(event.image_url);
        return;
      }
      const { data } = await imageServiceProxy.getByEntity('event', event.id);
      if (data) {
        setEventImageUrl(data);
      }
    } catch (e) { console.error('Error loading event image:', e); }
  }, [event]);

  // Load club images
  const loadClubImages = useCallback(async (clubIds) => {
    if (!clubIds || clubIds.length === 0) return;
    try {
      const imgs = {};
      for (const cid of clubIds) {
        const { data: logoUrl } = await storageServiceProxy.getClubLogo(cid);
        if (logoUrl) imgs[cid] = logoUrl;
      }
      setClubImages(imgs);
    } catch (e) { console.error('Error loading club images:', e); }
  }, []);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await eventGymnastService.getEventSummary(event.id);
      if (error) throw new Error(error);
      setSummary(data || []);
      const clubIds = (data || []).map(d => d.club?.id).filter(Boolean);
      loadClubImages(clubIds);
    } catch (err) {
      console.error('Error cargando resumen:', err);
    } finally {
      setLoading(false);
    }
  }, [event.id, loadClubImages]);

  useEffect(() => {
    loadSummary();
    loadEventImage();
  }, [loadSummary, loadEventImage]);

  const totalAthletes = useMemo(() => {
    return summary.reduce((sum, club) => sum + club.count, 0);
  }, [summary]);

  // Niveles del club seleccionado
  const selectedClubLevels = useMemo(() => {
    if (!selectedClub) return [];
    const levels = new Set();
    selectedClub.gymnasts.forEach(g => {
      if (g.level) levels.add(g.level);
    });
    return [...levels].sort();
  }, [selectedClub]);

  // Atletas filtradas del club seleccionado en el modal
  const filteredModalGymnasts = useMemo(() => {
    if (!selectedClub) return [];
    let list = selectedClub.gymnasts;
    if (modalSearch.trim()) {
      const q = modalSearch.toLowerCase();
      list = list.filter(g => (g.name || '').toLowerCase().includes(q));
    }
    if (modalFilterLevel !== 'all') {
      list = list.filter(g => g.level === modalFilterLevel);
    }
    return list;
  }, [selectedClub, modalSearch, modalFilterLevel]);

  const openClubModal = (clubData) => {
    setSelectedClub(clubData);
    setModalFilterLevel('all');
    setModalSearch('');
  };

  const closeModal = () => {
    setSelectedClub(null);
    setModalFilterLevel('all');
    setModalSearch('');
  };

  const getInitials = (name) => name?.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || '?';

  if (loading) {
    return (
      <div className="oea-loading">
        <div className="od-spinner" />
        <p>Cargando atletas del evento...</p>
      </div>
    );
  }

  return (
    <div className="oea-container">
      {/* Header */}
      <div className="oea-header">
        <button className="oea-back-btn" onClick={onBack}>
          <ArrowLeft size={18} /> Volver
        </button>
        <div className="oea-header-info">
          <div className="oea-event-identity">
            <div className="oea-event-avatar">
              {eventImageUrl ? (
                <img src={eventImageUrl} alt={event.name} />
              ) : (
                <span>🏆</span>
              )}
            </div>
            <div>
              <h2 className="oea-title">Atletas Inscritas</h2>
              <p className="oea-subtitle">{event.name}</p>
              <p className="oea-date">
                {event.date ? new Date(event.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                {event.location ? ` • ${event.location}` : ''}
              </p>
            </div>
          </div>
        </div>
        <button className="oea-refresh-btn" onClick={loadSummary} title="Actualizar">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Stats */}
      <div className="oea-stats">
        <div className="oea-stat oea-stat-blue">
          <Building2 size={20} />
          <div>
            <span className="oea-stat-value">{summary.length}</span>
            <span className="oea-stat-label">Clubes</span>
          </div>
        </div>
        <div className="oea-stat oea-stat-green">
          <Users size={20} />
          <div>
            <span className="oea-stat-value">{totalAthletes}</span>
            <span className="oea-stat-label">Atletas inscritas</span>
          </div>
        </div>
      </div>

      {/* Club list */}
      {summary.length === 0 ? (
        <div className="oea-empty">
          <Building2 size={48} />
          <p>No hay atletas inscritas en este evento</p>
          <span>Los clubes deben inscribir sus atletas desde su panel</span>
        </div>
      ) : (
        <div className="oea-clubs-list">
          {summary.map(clubData => {
            const clubId = clubData.club?.id;
            const clubLogo = clubImages[clubId];
            const levelMap = {};
            clubData.gymnasts.forEach(g => {
              const lvl = g.level || 'Sin nivel';
              levelMap[lvl] = (levelMap[lvl] || 0) + 1;
            });

            return (
              <div key={clubId} className="oea-club-row oea-club-row-clickable" onClick={() => openClubModal(clubData)}>
                <div className="oea-club-avatar">
                  {clubLogo ? (
                    <img src={clubLogo} alt={clubData.club?.name || ''} />
                  ) : (
                    <span>{getInitials(clubData.club?.name || '')}</span>
                  )}
                </div>
                <div className="oea-club-info">
                  <h3>{clubData.club?.name || 'Club'}</h3>
                  <p>
                    {clubData.club?.location || ''}
                    {clubData.club?.email ? (clubData.club?.location ? ' • ' : '') + clubData.club.email : ''}
                  </p>
                  <div className="oea-club-levels-preview">
                    {Object.entries(levelMap).slice(0, 4).map(([lvl, count]) => (
                      <span key={lvl} className="oea-mini-level">{lvl} ({count})</span>
                    ))}
                    {Object.keys(levelMap).length > 4 && (
                      <span className="oea-mini-level oea-mini-more">+{Object.keys(levelMap).length - 4} más</span>
                    )}
                  </div>
                </div>
                <div className="oea-club-count-col">
                  <span className="oea-count-badge">{clubData.count}</span>
                  <span className="oea-count-label">atleta{clubData.count !== 1 ? 's' : ''}</span>
                </div>
                <div className="oea-club-arrow">
                  <Eye size={16} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ========== MODAL: Atletas del club ========== */}
      {selectedClub && (
        <div className="oea-modal-overlay" onClick={closeModal}>
          <div className="oea-modal" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="oea-modal-header">
              <div className="oea-modal-club-identity">
                <div className="oea-modal-club-avatar">
                  {clubImages[selectedClub.club?.id] ? (
                    <img src={clubImages[selectedClub.club?.id]} alt={selectedClub.club?.name || ''} />
                  ) : (
                    <span>{getInitials(selectedClub.club?.name || '')}</span>
                  )}
                </div>
                <div>
                  <h3>{selectedClub.club?.name || 'Club'}</h3>
                  <p>{selectedClub.count} atleta{selectedClub.count !== 1 ? 's' : ''} inscrita{selectedClub.count !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button className="oea-modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            {/* Modal filters */}
            <div className="oea-modal-filters">
              <div className="oea-modal-search">
                <Search size={15} />
                <input
                  type="text"
                  value={modalSearch}
                  onChange={(e) => setModalSearch(e.target.value)}
                  placeholder="Buscar atleta..."
                />
              </div>
              {selectedClubLevels.length > 0 && (
                <div className="oea-modal-filter-select">
                  <Filter size={14} />
                  <select value={modalFilterLevel} onChange={(e) => setModalFilterLevel(e.target.value)}>
                    <option value="all">Todos los niveles ({selectedClub.count})</option>
                    {selectedClubLevels.map(lvl => {
                      const count = selectedClub.gymnasts.filter(g => g.level === lvl).length;
                      return <option key={lvl} value={lvl}>{lvl} ({count})</option>;
                    })}
                  </select>
                </div>
              )}
            </div>

            {/* Modal content: table */}
            <div className="oea-modal-body">
              {filteredModalGymnasts.length === 0 ? (
                <div className="oea-modal-empty">
                  <Users size={32} />
                  <p>No se encontraron atletas con estos filtros</p>
                </div>
              ) : (
                <table className="oea-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nombre</th>
                      <th>Nivel</th>
                      <th>Franja</th>
                      <th>Edad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModalGymnasts.map((gymnast, idx) => (
                      <tr key={gymnast.id}>
                        <td className="oea-td-num">{idx + 1}</td>
                        <td className="oea-td-name">{gymnast.name}</td>
                        <td>
                          {gymnast.level ? (
                            <span className="oea-level-badge">{gymnast.level}</span>
                          ) : '—'}
                        </td>
                        <td>
                          {gymnast.franja ? (
                            <span className="oea-franja-badge">{gymnast.franja}</span>
                          ) : '—'}
                        </td>
                        <td>{calculateAge(gymnast.birth_date) ?? calculateAge(gymnast.date_of_birth) ?? gymnast.age ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal footer: level mini stats */}
            {filteredModalGymnasts.length > 0 && (
              <div className="oea-modal-footer">
                <span className="oea-modal-footer-label">Resumen:</span>
                {(() => {
                  const map = {};
                  filteredModalGymnasts.forEach(g => {
                    const lvl = g.level || 'Sin nivel';
                    map[lvl] = (map[lvl] || 0) + 1;
                  });
                  return Object.entries(map).map(([lvl, count]) => (
                    <span key={lvl} className="oea-mini-stat">{lvl}: <strong>{count}</strong></span>
                  ));
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerEventAthletes;
